import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, DollarSign, Users, Link as LinkIcon, ArrowRight, Copy, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtUSD(cents) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ReferralDashboard() {
  const nav = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("parking_pro_email") || "");
  const [emailInput, setEmailInput] = useState(email);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async (target) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/earnings`, { email: target });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) load(email);
  }, [email]);

  const createLink = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/referral/create`, { email, percent_off: 15 });
      toast.success(res.data.already_existed ? "Existing link returned" : "Referral link created");
      load(email);
    } catch {
      toast.error("Could not create link");
    } finally {
      setBusy(false);
    }
  };

  const connectStripe = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/referral/connect`, {
        email,
        return_url: `${origin}/referrals?onboarded=1`,
        refresh_url: `${origin}/referrals?refresh=1`,
      });
      if (res.data.onboarding_url) window.location.href = res.data.onboarding_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Stripe Connect unavailable";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const payout = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/referral/payout`, { email });
      toast.success(`Paid out ${fmtUSD(res.data.amount_cents)}`);
      load(email);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Payout failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = data?.code ? `${window.location.origin}/r/${data.code}` : "";

  return (
    <div className="min-h-screen w-full grid-bg" data-testid="referral-dashboard">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#F0ABFC]">
              <Users size={12} /> REFERRALS · YOUR EARNINGS
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white mt-1">
              Refer.{" "}
              <span className="text-[#F0ABFC]" style={{ textShadow: "0 0 22px rgba(240,171,252,0.5)" }}>Earn.</span>
            </h1>
            <p className="text-xs text-white/40 tracking-wider mt-1">
              Share your link · buyers get 15% off · you earn $1.00 per confirmed sale
            </p>
          </div>
          <button
            onClick={() => nav("/")}
            className="px-4 py-2 border border-white/20 text-white/60 hover:border-cyan-300 hover:text-cyan-300 text-[10px] tracking-[0.3em]"
          >
            ← BACK TO GAME
          </button>
        </div>

        {/* Email gate */}
        {!email && (
          <div className="border border-white/10 bg-black/60 backdrop-blur-md p-6" data-testid="referral-email-gate">
            <div className="text-xs tracking-[0.3em] text-white/70 mb-3">Enter the email you want the referral tied to</div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                data-testid="referral-my-email"
                type="email"
                placeholder="you@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="flex-1 min-w-[220px] bg-transparent border-0 border-b border-white/20 rounded-none focus:outline-none focus:border-[#F0ABFC] font-mono text-white py-2 px-1 text-xs"
              />
              <button
                data-testid="referral-my-email-submit"
                disabled={!emailInput || !emailInput.includes("@")}
                onClick={() => {
                  const e = emailInput.trim().toLowerCase();
                  localStorage.setItem("parking_pro_email", e);
                  setEmail(e);
                }}
                className="px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
              >
                CONTINUE →
              </button>
            </div>
          </div>
        )}

        {email && (
          <>
            {loading && <div className="text-white/40 text-xs tracking-[0.3em]">LOADING…</div>}
            {!loading && !data?.exists && (
              <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="no-referral">
                <div className="text-lg tracking-widest text-white/80 mb-2">You don't have a referral link yet</div>
                <p className="text-xs text-white/50 mb-4">Create one — it takes 2 seconds. Share the URL anywhere.</p>
                <button
                  data-testid="referral-create"
                  disabled={busy}
                  onClick={createLink}
                  className="px-5 py-3 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40 flex items-center gap-2"
                >
                  <LinkIcon size={12} /> CREATE MY REFERRAL LINK
                </button>
              </div>
            )}

            {!loading && data?.exists && (
              <div className="space-y-6">
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ref-kpis">
                  <Kpi label="CLICKS" value={data.clicks} accent="#00F0FF" />
                  <Kpi label="CONVERSIONS" value={data.conversions} accent="#39FF14" />
                  <Kpi label="EARNED" value={fmtUSD(data.credit_cents)} accent="#FFD400" />
                  <Kpi label="PAYABLE" value={fmtUSD(data.payable_cents)} accent="#F0ABFC" testid="kpi-payable" />
                </div>

                {/* Share link */}
                <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6">
                  <div className="text-[10px] tracking-[0.4em] text-white/50 mb-3">YOUR LINK</div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 font-mono text-sm text-white break-all border border-white/10 bg-black/60 px-3 py-2">
                      {shareUrl}
                    </div>
                    <button
                      data-testid="copy-link"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(shareUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        } catch {}
                      }}
                      className="px-3 py-2 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2"
                    >
                      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />} {copied ? "COPIED" : "COPY"}
                    </button>
                  </div>
                  <div className="text-[10px] tracking-[0.3em] text-white/40 mt-2">Coupon <span className="text-[#F0ABFC]">{data.coupon_id}</span> · 15% OFF</div>
                </div>

                {/* Payout */}
                <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FFD400] mb-3">
                    <DollarSign size={12} /> PAYOUT
                  </div>
                  {!data.stripe_account_id ? (
                    <>
                      <p className="text-xs text-white/60 mb-3">Connect a Stripe account to cash out your <span className="text-[#F0ABFC]">{fmtUSD(data.payable_cents)}</span> balance.</p>
                      <button
                        data-testid="connect-stripe"
                        disabled={busy}
                        onClick={connectStripe}
                        className="px-5 py-3 border border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40 flex items-center gap-2"
                      >
                        <Sparkles size={12} /> CONNECT STRIPE <ArrowRight size={12} />
                      </button>
                    </>
                  ) : !data.onboarding_complete ? (
                    <>
                      <p className="text-xs text-white/60 mb-3">Onboarding in progress. Finish setup to enable payouts.</p>
                      <button
                        data-testid="connect-stripe"
                        disabled={busy}
                        onClick={connectStripe}
                        className="px-5 py-3 border border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40 flex items-center gap-2"
                      >
                        RESUME ONBOARDING <ArrowRight size={12} />
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-white/60 mb-3">Balance available: <span className="text-[#F0ABFC] font-bold">{fmtUSD(data.payable_cents)}</span> — paid out to your connected Stripe account.</p>
                      <button
                        data-testid="payout"
                        disabled={busy || data.payable_cents <= 0}
                        onClick={payout}
                        className="px-5 py-3 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-30 flex items-center gap-2"
                      >
                        <DollarSign size={12} /> CASH OUT {fmtUSD(data.payable_cents)}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="border border-white/10 bg-black/40 backdrop-blur-md p-4" data-testid={testid} style={{ boxShadow: `inset 0 0 24px ${accent}18` }}>
      <div className="text-[10px] tracking-[0.3em]" style={{ color: accent }}>{label}</div>
      <div className="font-display text-2xl md:text-3xl tabular-nums text-white mt-1" style={{ textShadow: `0 0 14px ${accent}55` }}>{value}</div>
    </div>
  );
}
